import React, { useEffect, useState } from 'react'

import { SizedGoodQuantity, type DynamicSizedQuantities } from '@vp/lat-react-component-library'
import { Box, useScreenClass } from '@vp/swan'
import { useLocalizations } from '@vp/ubik-localizations-provider'

import { QuantitySelectorLoader } from './QuantitySelectorLoader'
import QuantityPrice from './QuantityPrice'
import { useQuantity } from '../../contexts/QuantityContext'
import { useInventory } from '../../../root/contexts/InventoryContext'
import { usePricingContext } from '../../../root/contexts/PricingContext'
import useCrossSizeMoq from '../../../../hooks/queries/useCrossSizeMoq'
import type { QuantityData, SizeAvailability } from '../../../../types/Inventory'
import { OutOfStockToast } from '../OutOfStockToast'
import { useProject } from '../../../root/contexts/ProjectContext'
import { scrollToTopToast } from '../../../../utils/ScrollHelpers'
import { useAppState } from '../../../root/contexts/AppContext'
import SizeFitContainer from './SizeFitContainer'
import { useAlertState } from '../../../root/contexts/AlertContext'
import { AddToCartValidationToast } from '../AddToCartValidationToast'
import { useTracking } from '../../../root/contexts/TrackingContext'
import { TrackingLabel } from '../../../root/contexts/TrackingContext/TrackingConstants'

export const ConnectedSizedGoodQuantitySelector: React.FC = () => {
  const { t } = useLocalizations()
  const inventoryState = useInventory()
  const { isInitialized: isPricingContextInitialized } = usePricingContext()
  const {
    projectCount,
    projectNumber,
    isPrimaryProject,
    sizeFitKey,
    workId,
    mpvId,
    productName,
    productKey,
    productVersion,
    productViewTracked,
    setProductViewTracked,
  } = useProject()
  const { crossSizeMoq, isLoading: isCrossSizeMoqLoading } = useCrossSizeMoq()
  const { inputQuantity, updateQuantity } = useQuantity()
  const { culture } = useAppState()
  const { projectQtyErrors } = useAlertState()
  const [showAddToCartValidationToast, setShowAddToCartValidationToast] = useState(false)

  const { trackProductViewed } = useTracking()
  const screenClass = useScreenClass()

  const isLoading = !inventoryState.isInventoryStateInitialized || !isPricingContextInitialized || isCrossSizeMoqLoading

  useEffect(() => {
    if (productViewTracked) return

    if (!isLoading) {
      trackProductViewed(TrackingLabel.QSP_PRODUCT_VIEWED, mpvId, productName, productKey, productVersion)
      setProductViewTracked(true)
    }
  }, [isLoading])

  useEffect(() => {
    const projectHasInvalidQty = projectQtyErrors.includes(workId)
    setShowAddToCartValidationToast(projectHasInvalidQty)
  }, [projectQtyErrors, projectQtyErrors.length, workId])

  const onChange = (quantityData: QuantityData, isValid: boolean): void => {
    updateQuantity(quantityData, isValid)
    if (showAddToCartValidationToast && isValid) {
      setShowAddToCartValidationToast(false)
    }
  }

  const isOutOfStock = (availability: SizeAvailability[]): boolean => {
    return availability.map((a) => a.numAvailable).reduce((sum, current) => sum + current, 0) < crossSizeMoq
  }

  const getExpectedDate = (availability: SizeAvailability[]): Date | undefined => {
    let lowestDate: Date | undefined = undefined
    for (const sizeAvailability of availability) {
      if (sizeAvailability.expectedDate && (!lowestDate || sizeAvailability.expectedDate < lowestDate)) {
        lowestDate = sizeAvailability.expectedDate
      }
    }
    return lowestDate
  }

  useEffect(() => {
    // Primary project is OOS, scroll "nearest"
    if (!isLoading && screenClass === 'xs' && projectCount === 1) {
      scrollToTopToast('qsp-project-quantity-section-alert', 1000, 'nearest')
    }

    // OOS projects will have an invalid quantity/add to cart validation toast,
    // scroll "center" to make sure the related products title doesn't block it.
    else if (showAddToCartValidationToast) {
      scrollToTopToast('qsp-project-quantity-section-alert', 0, 'center')
    }
  }, [isLoading, screenClass, showAddToCartValidationToast, projectQtyErrors])

  return (
    <>
      {isLoading && (
        <Box className="qsp-project-quantity-section">
          <QuantitySelectorLoader />
        </Box>
      )}
      {!isLoading && (
        <>
          {!isPrimaryProject && showAddToCartValidationToast && (
            <Box className="qsp-project-quantity-section qsp-project-quantity-section-alert" my={3}>
              <AddToCartValidationToast key={projectNumber} projectNumber={projectNumber} />
            </Box>
          )}
          {isOutOfStock(inventoryState.inventory as SizeAvailability[]) && (
            <Box className="qsp-project-quantity-section qsp-project-quantity-section-alert" my={3}>
              <OutOfStockToast expectedDate={getExpectedDate(inventoryState.inventory as SizeAvailability[])} />
            </Box>
          )}
          <QuantityPrice />
          {sizeFitKey && <SizeFitContainer />}
          <Box
            className="qsp-project-quantity-section qsp-project-quantity-section-sized-selector"
            data-testid="sizegood-quantity-selector"
            mb="between-subsections"
          >
            <SizedGoodQuantity
              inputQuantityData={inputQuantity as DynamicSizedQuantities}
              sizeAvailabilityData={inventoryState.inventory as SizeAvailability[]}
              moq={crossSizeMoq}
              onChange={onChange}
              quantityRequired={true}
              hideTitle={true}
              hideAdjustQuantity={false}
              hideInsuficientStock={true}
              localizedText={{
                invalidQtyMessage: t('quantity-selector.adjust-quantity'),
              }}
              columns={4}
              locale={culture}
            />
          </Box>
        </>
      )}{' '}
    </>
  )
}
